// Org-level MCP servers (MCP_SERVERS_PLAN.md): the registry rows that agent
// and service-definition bindings resolve against. Mirrors
// backend/api/services/mcp_server_service.py serialize() — credentials never
// come back from the API, only has_credential.
import { useCallback, useEffect, useState } from 'react';
import useApi from '../api';

export type McpAuthMode = 'oauth' | 'none' | 'api_key' | 'aimdoc_identity';

/** OAuth sign-in state of a server connected through its own login. */
export interface McpServerOAuth {
  status: 'active' | 'needs_reauth';
  connected_by_email: string | null;
  connected_at: string | null;
  scopes: string[] | null;
}

export interface McpServerTool {
  name: string;
  description?: string;
  input_schema?: Record<string, any>;
  read_only?: boolean;
}

export interface McpServer {
  id: string;
  name: string;
  description?: string | null;
  url: string;
  auth_mode: McpAuthMode;
  has_credential: boolean;
  /** Present only for auth_mode === 'oauth'. */
  oauth?: McpServerOAuth | null;
  enabled: boolean;
  tools: McpServerTool[];
  tools_discovered_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * A consumer's reference to an org server, stored in Agent.tools.mcp and
 * ServiceDefinition.tools.mcp. The slug namespaces the LLM tool names
 * ("{slug}_{tool}") and freezes once the binding is saved; enabled_tools
 * filters the server catalog (null/absent = every discovered tool).
 */
export interface McpBinding {
  mcp_server_id: string;
  slug: string;
  enabled?: boolean;
  enabled_tools?: string[] | null;
}

/**
 * Pre-registry entry shape (url + optional api_key inline). Only seen on
 * configs that pre-date migration 0052; rendered read-only until removed.
 */
export interface LegacyInlineMcpConnection {
  id?: string;
  name: string;
  slug?: string;
  url: string;
  auth_mode?: McpAuthMode;
  enabled?: boolean;
  tools?: McpServerTool[];
  enabled_tools?: string[] | null;
}

export type McpEntry = McpBinding | LegacyInlineMcpConnection;

export function isMcpBinding(entry: any): entry is McpBinding {
  return Boolean(entry && typeof entry === 'object' && entry.mcp_server_id);
}

export const AUTH_MODE_LABELS: Record<McpAuthMode, string> = {
  oauth: 'Sign in',
  none: 'No auth',
  api_key: 'API key',
  aimdoc_identity: 'Aimdoc identity',
};

export const AUTH_MODE_OPTIONS: { value: McpAuthMode; label: string; helper: string }[] = [
  {
    value: 'oauth',
    label: 'Sign in with your product',
    helper:
      "Aimdoc signs you in through your product's own login (standard MCP OAuth); the connection acts as you for setup and testing.",
  },
  {
    value: 'none',
    label: 'None',
    helper: 'No authentication. Only for public MCP servers.',
  },
  {
    value: 'api_key',
    label: 'API key',
    helper: 'A static API key sent as a Bearer token on every call. Stored once for the organization; never shown again.',
  },
  {
    value: 'aimdoc_identity',
    label: 'Aimdoc identity',
    helper:
      'Aimdoc signs a short-lived identity token for the current visitor using your identity-verification signing secret (Integrations → Identity Verification). Your MCP server verifies it with the same secret — no per-user login needed. The identity_tier claim says how the visitor was authenticated: "verified" (signed identify), "email_reply" (authenticated reply to an email your agent initiated), or "anonymous".',
  },
];

/** Enabled tools of a binding, resolved against its server's catalog. */
export function bindingEnabledToolNames(binding: McpBinding, server: McpServer | undefined): string[] {
  const all = (server?.tools || []).map((tool) => tool.name);
  if (!binding.enabled_tools) return all;
  return binding.enabled_tools.filter((name) => all.includes(name));
}

export function errorDetail(error: any, fallback: string): string {
  const detail = error?.detail ?? error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return fallback;
}

/**
 * The organization's MCP servers. Each consumer of the hook fetches on mount
 * (agent-builder tabs unmount when inactive, so switching tabs refreshes).
 */
export function useMcpServers() {
  const { get } = useApi();
  const [servers, setServers] = useState<McpServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await get('/mcp-servers');
      setServers(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err: any) {
      setError(errorDetail(err, 'Could not load MCP servers.'));
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const upsert = useCallback((server: McpServer) => {
    setServers((current) => {
      const exists = current.some((item) => item.id === server.id);
      return exists ? current.map((item) => (item.id === server.id ? server : item)) : [...current, server];
    });
  }, []);

  const removeLocal = useCallback((id: string) => {
    setServers((current) => current.filter((item) => item.id !== id));
  }, []);

  return { servers, loading, error, refresh, upsert, removeLocal };
}
